import React, { useState } from "react";
import axios from "axios";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AttachFile, HelpOutline, Send } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(3),
        overflowY: "auto",
        ...theme.scrollbarStyles,
    },
    infoBox: {
        backgroundColor: theme.palette.type === "dark" ? "rgba(45,123,232,0.08)" : "#e8f0fe",
        color: theme.palette.type === "dark" ? "#90b8f8" : "#1a4f96",
        border: `1px solid ${theme.palette.type === "dark" ? "rgba(45,123,232,0.2)" : "#b3cef5"}`,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(3),
        fontSize: "0.875rem",
        maxWidth: 560,
    },
    tokenSection: {
        maxWidth: 560,
        marginBottom: theme.spacing(3),
    },
    formContainer: {
        maxWidth: 560,
    },
    textRight: {
        textAlign: "right",
    },
    tabContent: {
        marginTop: theme.spacing(3),
    },
    fileRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        flexWrap: "wrap",
    },
    fileName: {
        fontSize: "0.875rem",
        color: theme.palette.text.secondary,
    },
    input: {
        display: "none",
    },
    docsSection: {
        maxWidth: 560,
        marginTop: theme.spacing(4),
    },
    codeBlock: {
        backgroundColor: theme.palette.type === "dark" ? "#1a1a2e" : "#f5f5f5",
        color: theme.palette.type === "dark" ? "#e0e0e0" : "#333",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(1.5),
        fontFamily: "monospace",
        fontSize: "0.78rem",
        overflowX: "auto",
        whiteSpace: "pre",
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    docsLabel: {
        fontWeight: 600,
        fontSize: "0.8rem",
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(0.5),
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const MessagesAPI = () => {
    const classes = useStyles();
    const [tabValue, setTabValue] = useState(0);
    const [file, setFile] = useState(null);
    const [token, setToken] = useState("");

    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const handleTabChange = (_, newValue) => {
        setTabValue(newValue);
    };

    const handleSendTextMessage = async (values) => {
        if (!token) {
            toast.warning("Informe o token da conexão antes de enviar.");
            return;
        }
        try {
            await axios.request({
                method: "POST",
                url: `${backendUrl}/api/messages/send`,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                data: { number: values.number, body: values.body, ...(values.name ? { name: values.name } : {}) },
            });
            toast.success("Mensagem de texto enviada com sucesso.");
        } catch (error) {
            toastError(error);
        }
    };

    const handleSendMediaMessage = async (values) => {
        if (!token) {
            toast.warning("Informe o token da conexão antes de enviar.");
            return;
        }
        if (!file) {
            toast.warning("Selecione um arquivo antes de enviar.");
            return;
        }
        try {
            const data = new FormData();
            data.append("number", values.number);
            data.append("body", file.name);
            data.append("medias", file);
            if (values.name) data.append("name", values.name);

            await axios.request({
                method: "POST",
                url: `${backendUrl}/api/messages/send`,
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                },
                data,
            });
            toast.success("Mensagem de mídia enviada com sucesso.");
            setFile(null);
        } catch (error) {
            toastError(error);
        }
    };

    const renderFormMessageText = () => (
        <Formik
            initialValues={{ number: "", name: "", body: "" }}
            onSubmit={async (values, actions) => {
                await handleSendTextMessage(values);
                actions.setSubmitting(false);
                actions.resetForm();
            }}
        >
            {({ isSubmitting }) => (
                <Form className={classes.formContainer}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <Field
                                as={TextField}
                                label="Número (com DDD e código do país)"
                                name="number"
                                variant="outlined"
                                fullWidth
                                required
                                helperText="Ex: 5511999999999"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Field
                                as={TextField}
                                label="Nome do contato (opcional)"
                                name="name"
                                variant="outlined"
                                fullWidth
                                helperText="Usado apenas se o contato não existir ainda"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Field
                                as={TextField}
                                label="Mensagem"
                                name="body"
                                variant="outlined"
                                fullWidth
                                multiline
                                rows={4}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} className={classes.textRight}>
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                                disabled={isSubmitting || !token}
                                startIcon={isSubmitting ? <CircularProgress size={16} /> : <Send />}
                            >
                                Enviar Mensagem
                            </Button>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </Formik>
    );

    const renderFormMessageMedia = () => (
        <Formik
            initialValues={{ number: "", name: "" }}
            onSubmit={async (values, actions) => {
                await handleSendMediaMessage(values);
                actions.setSubmitting(false);
                actions.resetForm();
            }}
        >
            {({ isSubmitting }) => (
                <Form className={classes.formContainer}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <Field
                                as={TextField}
                                label="Número (com DDD e código do país)"
                                name="number"
                                variant="outlined"
                                fullWidth
                                required
                                helperText="Ex: 5511999999999"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Field
                                as={TextField}
                                label="Nome do contato (opcional)"
                                name="name"
                                variant="outlined"
                                fullWidth
                                helperText="Usado apenas se o contato não existir ainda"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <div className={classes.fileRow}>
                                <input
                                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                                    className={classes.input}
                                    id="api-file-upload"
                                    type="file"
                                    onChange={(e) => setFile(e.target.files[0])}
                                />
                                <label htmlFor="api-file-upload">
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        component="span"
                                        startIcon={<AttachFile />}
                                    >
                                        {file ? "Trocar arquivo" : "Selecionar arquivo"}
                                    </Button>
                                </label>
                                {file && (
                                    <Typography className={classes.fileName}>
                                        {file.name}
                                    </Typography>
                                )}
                            </div>
                        </Grid>
                        <Grid item xs={12} className={classes.textRight}>
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                                disabled={isSubmitting || !file || !token}
                                startIcon={isSubmitting ? <CircularProgress size={16} /> : <Send />}
                            >
                                Enviar Arquivo
                            </Button>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </Formik>
    );

    return (
        <MainContainer>
            <MainHeader>
                <Title>API — Envio de Mensagens</Title>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">

                {/* Info box */}
                <Box className={classes.infoBox}>
                    <Typography variant="body2">
                        <strong>O que é isso?</strong> Esta página permite enviar mensagens WhatsApp via API externa — útil para integrar com CRMs, ERPs, automações (n8n, webhooks, etc.). Cada mensagem enviada cria ou atualiza automaticamente o contato e abre um ticket no sistema de atendimento.
                    </Typography>
                </Box>

                {/* Token compartilhado entre as abas */}
                <Box className={classes.tokenSection}>
                    <TextField
                        label="Token da Conexão"
                        variant="outlined"
                        fullWidth
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        helperText="Defina o token ao editar uma conexão WhatsApp (campo Token, com botão Gerar). Identifica por qual número a mensagem será enviada."
                        InputProps={{
                            endAdornment: (
                                <Tooltip
                                    title="Acesse Conexões > Editar uma conexão, role até o campo Token e clique no botão Gerar para criar um token. Salve a conexão e cole o valor aqui."
                                    placement="top"
                                    arrow
                                >
                                    <HelpOutline style={{ fontSize: 18, opacity: 0.5, cursor: "default" }} />
                                </Tooltip>
                            ),
                        }}
                    />
                </Box>

                <Divider style={{ marginBottom: 0 }} />

                {/* Tabs */}
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label="Mensagem de Texto" />
                    <Tab label="Mensagem de Mídia" />
                </Tabs>

                <div className={classes.tabContent}>
                    {tabValue === 0 && renderFormMessageText()}
                    {tabValue === 1 && renderFormMessageMedia()}
                </div>

                {/* Documentação */}
                <Box className={classes.docsSection}>
                    <Divider style={{ marginBottom: 24 }} />
                    <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                        Documentação do Endpoint
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        <code>POST {backendUrl}/api/messages/send</code>
                    </Typography>

                    <Typography className={classes.docsLabel}>Texto (JSON)</Typography>
                    <div className={classes.codeBlock}>{`curl -X POST "${backendUrl}/api/messages/send" \\
  -H "Authorization: Bearer SEU_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"number":"5511999999999","body":"Olá, tudo bem?","name":"João Silva"}'`}</div>

                    <Typography className={classes.docsLabel}>Mídia (multipart)</Typography>
                    <div className={classes.codeBlock}>{`curl -X POST "${backendUrl}/api/messages/send" \\
  -H "Authorization: Bearer SEU_TOKEN" \\
  -F "number=5511999999999" \\
  -F "medias=@/caminho/para/arquivo.pdf"`}</div>

                    <Typography variant="caption" color="textSecondary">
                        O número deve ser informado com código do país e DDD, sem espaços ou caracteres especiais. Ex: <code>5511999999999</code>
                    </Typography>
                </Box>

            </Paper>
        </MainContainer>
    );
};

export default MessagesAPI;
